/**
 * Reply-all addressing for one Patchbay conversation pane.
 *
 * Desk records a colleague's Outlook reply as an inbound turn. Direction alone therefore cannot decide who a
 * reply is for: the author of the newest external inbound turn is To (#683), everyone else on that turn goes in
 * Cc, and the ticket contact stays in Cc when they did not write it. Intake and robot addresses are dropped.
 * Personal staff mailboxes may ride in Cc, but no Ewee/SmartPBX address can become To on a customer pane. A
 * contractor/vendor pane is the exception: its party can hold an Ewee mailbox (the Bell rep on @e-wee.com), so a
 * non-staff writer there, or the address we last sent to there, may be To. Intake and robot addresses never are.
 *
 * A one-off writer used to stay in Cc with the Desk contact in To (Riley PR #66 / #22919). #683 answers the
 * person who wrote, including on the first reply and across domains (C-23872: Rick wrote, Kyle was in Cc and was
 * the previous send's To). Contractor and vendor panes still address the writer on that pane; the customer
 * contact is not added to those Cc lists (#22888).
 *
 * Owner: web-case-composer (slice 16).
 */

const LEGACY_OUR_DOMAINS = ['e-wee.com', 'smartpbx.io']
const INTAKE_ALIASES = new Set(['helpdesk', 'help', 'support', 'desk', 'servicedesk', 'service', 'itsupport', 'tickets', 'ticket'])
const ROBOT =
  /^(?:pbx@smartpbx\.io$|no[-_.]?reply|do[-_.]?not[-_.]?reply|noreply|mailer-daemon|postmaster@|notifications?@|alerts?@|marketing@|newsletter@|bounce)/i
const EMAIL = /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)+/gi
const NDR_SUBJECT = /(?:delivery (?:status notification|failure|failed)|mail delivery failed|undeliver(?:able|ed)|returned to sender)/i
const NDR_FAILURE =
  /(?:could(?:n't|n’t| not) be delivered|was(?:n't|n’t| not) delivered|delivery (?:has )?failed|failed to (?:these recipients|deliver)|undeliverable|only accepts messages|rejected (?:it|the message|your message)|recipient rejected|delivery to .+ failed)/i
const NDR_RECIPIENT_FIELD =
  /(?:final-recipient|original-recipient|x-failed-recipients?|failed-recipient|recipient address)\s*:\s*(?:rfc822\s*;\s*)?([A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+)+)/gi
const NDR_LIST_HEADING =
  /(?:delivery has failed to these recipients|the following recipients? (?:could not be reached|were rejected)|message (?:could not|couldn't|couldn’t) be delivered to)/i
const MAX_CC = 10

export interface ReplyTurn {
  direction: 'in' | 'out' | 'draft' | 'note'
  author_kind: string
  from_addr?: string | null
  author_email?: string | null
  to?: string[]
  cc?: string[]
  sent_at?: string | null
  is_unsent_draft?: boolean
  subject?: string | null
  text_clean?: string | null
  is_auto_reply?: boolean
}

export interface ReplyRecipients {
  to: string[]
  cc: string[]
  removed: { address: string; reason: string }[]
}

export interface ReplyContext {
  audience: 'customer' | 'contractor' | 'vendor' | 'internal' | 'unclassified'
  /** The pane's own customer address, then the primary Desk contact. */
  contactEmail?: string | null
  /** This case's brand domains (`CaseDetail.brand.our_domains`), added to today's; absent for an unresolved brand. */
  ourDomains?: readonly string[]
  /** Case-wide addresses named as failed by an attached delivery-status notification. */
  rejectedAddresses?: ReadonlySet<string>
}

/** A bare, lower-case mailbox from either `Name <address>` or an already bare address. */
export function replyAddress(value?: string | null): string | null {
  if (!value) return null
  const angle = /<([^>\s]+@[^>\s]+)>/.exec(value)
  const plain = /([^\s<>"']+@[^\s<>"']+)/.exec(value)
  const email = (angle?.[1] ?? plain?.[1] ?? '')
    .trim()
    .replace(/[;,]+$/, '')
    .toLowerCase()
  return email.includes('@') ? email : null
}

function isOurDomain(address: string, context: ReplyContext): boolean {
  const domain = address.slice(address.lastIndexOf('@') + 1).toLowerCase()
  return (
    // Same as the API's reply_all._our_domains: today's domains stay ours on every brand, plus the case brand's own.
    [...LEGACY_OUR_DOMAINS, ...(context.ourDomains ?? [])].some((ours) => domain === ours || domain.endsWith(`.${ours}`)) ||
    domain.endsWith('.zohodesk.com')
  )
}

function removalReason(address: string, context: ReplyContext): string | null {
  const email = replyAddress(address)
  if (!email) return 'invalid address'
  const [local = '', domain = ''] = email.split('@')
  if (domain.endsWith('.zohodesk.com') || (isOurDomain(email, context) && INTAKE_ALIASES.has(local))) return 'intake mailbox'
  if (ROBOT.test(email)) return 'robot recipient'
  if (context.rejectedAddresses?.has(email)) return 'delivery rejected'
  return null
}

/** Extract only addresses tied to an NDR failure phrase or a standard DSN recipient field. */
export function rejectedRecipientAddresses(turns: readonly ReplyTurn[]): ReadonlySet<string> {
  const rejected = new Set<string>()
  for (const turn of turns) {
    const subject = turn.subject ?? ''
    const body = turn.text_clean ?? ''
    const sender = replyAddress(turn.from_addr) ?? ''
    if (!(ROBOT.test(sender) || NDR_SUBJECT.test(subject) || (turn.is_auto_reply && NDR_FAILURE.test(body)))) continue

    NDR_RECIPIENT_FIELD.lastIndex = 0
    for (const match of body.matchAll(NDR_RECIPIENT_FIELD)) if (match[1]) rejected.add(match[1].toLowerCase())

    let listWindow = 0
    for (const line of body.split(/\r?\n/)) {
      const compact = line.replace(/\s+/g, ' ').trim()
      if (!compact) continue
      if (NDR_LIST_HEADING.test(compact)) listWindow = 3
      EMAIL.lastIndex = 0
      const addresses = [...compact.matchAll(EMAIL)].map((match) => match[0].toLowerCase())
      if (addresses.length && (NDR_FAILURE.test(compact) || listWindow)) for (const address of addresses) rejected.add(address)
      if (listWindow) listWindow -= 1
    }
  }
  return rejected
}

/** A contractor/vendor pane's party may sit on an Ewee mailbox (an internal rep), so that pane may address one. */
function partyPane(context: ReplyContext): boolean {
  return context.audience === 'contractor' || context.audience === 'vendor'
}

function externalWriter(turn: ReplyTurn, context: ReplyContext): string | null {
  if (turn.is_unsent_draft || turn.is_auto_reply || turn.direction !== 'in' || turn.author_kind === 'staff' || turn.author_kind === 'riley')
    return null
  // A Desk portal/web turn has no mail headers to reply-all to. Match Riley PR #66 by continuing back to the newest
  // external email instead of silently dropping that email's participants.
  if (!(turn.to?.length || turn.cc?.length)) return null
  const sender = replyAddress(turn.author_email) ?? replyAddress(turn.from_addr)
  return safeTo(sender, context)
}

function sentByUs(turn: ReplyTurn): boolean {
  if (turn.is_unsent_draft || turn.direction === 'draft' || turn.direction === 'note') return false
  // Outlook messages sent by a staff mailbox arrive in Desk as `in`; ordinary Desk replies are `out`.
  return turn.direction === 'out' || turn.author_kind === 'staff' || turn.author_kind === 'riley'
}

function newestExternal(turns: ReplyTurn[], context: ReplyContext): { turn: ReplyTurn; sender: string } | null {
  for (const turn of [...turns].reverse()) {
    const sender = externalWriter(turn, context)
    if (sender) return { turn, sender }
  }
  return null
}

function safeTo(address: string | null | undefined, context: ReplyContext): string | null {
  const email = replyAddress(address)
  return email && (partyPane(context) || !isOurDomain(email, context)) && !removalReason(email, context) ? email : null
}

function collectCc(
  to: string | null,
  addresses: (string | null | undefined)[],
  context: ReplyContext,
): Pick<ReplyRecipients, 'cc' | 'removed'> {
  const cc: string[] = []
  const removed: ReplyRecipients['removed'] = []
  const seen = new Set(to ? [to] : [])
  for (const raw of addresses) {
    const address = replyAddress(raw)
    if (!address) continue
    const why = removalReason(address, context)
    if (why) {
      if (!removed.some((item) => item.address === address)) removed.push({ address, reason: why })
      continue
    }
    if (seen.has(address)) continue
    seen.add(address)
    if (cc.length < MAX_CC) cc.push(address)
    else removed.push({ address, reason: `reply-all limit (${MAX_CC})` })
  }
  return { cc, removed }
}

/**
 * Address the pane's next reply. The newest external inbound author is To (#683). Everyone else on that turn is Cc,
 * and on a customer pane the ticket contact is Cc when they did not write it. Intake and robots are removed.
 */
export function replyAllRecipients(turns: ReplyTurn[], context: ReplyContext): ReplyRecipients {
  if (context.audience === 'internal' || context.audience === 'unclassified') return { to: [], cc: [], removed: [] }
  const resolvedContext = context.rejectedAddresses ? context : { ...context, rejectedAddresses: rejectedRecipientAddresses(turns) }
  const latest = newestExternal(turns, resolvedContext)
  const contact = safeTo(resolvedContext.contactEmail, resolvedContext)
  if (!latest) {
    const rawContact = replyAddress(resolvedContext.contactEmail)
    const removed =
      rawContact && resolvedContext.rejectedAddresses?.has(rawContact) ? [{ address: rawContact, reason: 'delivery rejected' }] : []
    return { to: contact ? [contact] : [], cc: [], removed }
  }

  const to = latest.sender
  const carried = [latest.sender, ...(latest.turn.to ?? []), ...(latest.turn.cc ?? [])]
  if (resolvedContext.audience === 'customer' && contact) carried.push(contact)
  const rest = collectCc(to, carried, resolvedContext)
  return { to: to ? [to] : [], ...rest }
}

/** Address an explicit Reply/Reply-all action to the selected message, using only evidence available at that turn. */
export function replyAllForTurn(turn: ReplyTurn, priorTurns: ReplyTurn[], context: ReplyContext): ReplyRecipients {
  if (context.audience === 'internal' || context.audience === 'unclassified') return { to: [], cc: [], removed: [] }
  if (sentByUs(turn)) {
    const direct = (turn.to ?? []).map((address) => safeTo(address, context)).filter((address): address is string => Boolean(address))
    const to = direct[0] ?? safeTo(context.contactEmail, context)
    const rest = collectCc(to, [...direct.slice(1), ...(turn.cc ?? [])], context)
    return { to: to ? [to] : [], ...rest }
  }
  // An explicit Reply on a headerless portal turn still has a safe target: the resolved customer contact. It must
  // not borrow Cc recipients from some other message because this action names this exact turn.
  if (!(turn.to?.length || turn.cc?.length)) {
    const to = safeTo(context.contactEmail, context)
    return { to: to ? [to] : [], cc: [], removed: [] }
  }
  return replyAllRecipients([...priorTurns.filter((candidate) => candidate !== turn), turn], context)
}
